import React, { FC, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import jwtDecode from 'jwt-decode';
import { useAuth } from 'src/hooks/useAuth';
import { THIconGear } from 'src/theme/media';
import { PasswordChangeForm } from 'src/features/auth/components';
import { getUserOnboardingRequest } from 'src/store/actions/user';
import { ProgressMeter } from 'src/components/ProgressMeter';
import { DecodedToken } from 'src/types/common';
import { validateAuthToken } from './validateAuthToken';
import { LoaderComponent } from 'src/components/Loader';
import * as S from './Styles';

export const PasswordChangePage: FC<unknown> = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const { hasPendingOnboarding, user, isAdmin } = useAuth();
  const query = new URLSearchParams(search);
  const authToken = query.get('token');
  const id = query.get('id');
  const resetPassword = query.get('reset_password');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthTokenExpired, setIsAuthTokenExpired] = useState<boolean>(false);

  const getTokenPurpose = (): DecodedToken['purpose'] => {
    if (!authToken) return undefined;
    try {
      return jwtDecode<DecodedToken>(authToken).purpose;
    } catch {
      return undefined;
    }
  };
  const isOnboarding = getTokenPurpose() === 'welcome' || hasPendingOnboarding;

  useEffect(() => {
    const fetchUserOnboarding = async () => {
      setIsLoading(true);
      try {
        if (authToken) {
          const { isValid, isReplacementSent } =
            await validateAuthToken(authToken);

          if (isReplacementSent) {
            return history.push('/expired-link');
          }

          setIsAuthTokenExpired(!isValid);
        } else if (id && !isAdmin) {
          dispatch(getUserOnboardingRequest(+id));
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserOnboarding();
  }, [authToken, id, isAdmin, dispatch, history]);

  useEffect(() => {
    if (user?.id > 0 && hasPendingOnboarding && !resetPassword) {
      history.push(`/onboarding/welcome${search}`);
    }
  }, [user, hasPendingOnboarding, resetPassword, history, search]);

  const handleNewPass = () => {
    history.push(`/forgotten_password`);
  };

  if (!authToken || !id) {
    history.push('/');
    return null;
  }

  if (isLoading) {
    return <LoaderComponent />;
  }

  if (isAuthTokenExpired) {
    history.push('/expired-link');
    return null;
  }

  return (
    <S.PageContainer>
      <S.PageTitle>
        {isOnboarding
          ? "Let's finish setting up your account."
          : 'Change your password'}
      </S.PageTitle>
      <S.PageContentContainer>
        <p>
          {isOnboarding
            ? 'Please enter a password to secure your account.'
            : 'Please choose a new password and enter it below.'}
        </p>
        {authToken && id && (
          <PasswordChangeForm authToken={authToken} userId={id} />
        )}
        <S.PageContentBottomContainer>
          <ProgressMeter hasOnboarding={isOnboarding} step={2} />
        </S.PageContentBottomContainer>
      </S.PageContentContainer>
      <S.GearImage src={THIconGear} />
    </S.PageContainer>
  );
};
